/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { Button, CircularProgress, Slider, TextField, withStyles } from '@material-ui/core'
import { css, jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { theme } from '../../../constants'
import OrganizationService from '../../../services/organization'
import * as Actions from '../../admin/actions'

const styles = {}

styles.blockingOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
`

styles.box = css`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: 700px;
  border-radius: 5px;
  padding: 30px 60px;
  background: white;
  border: 10px solid lightgray;
  pointer-events: all;
`

styles.title = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: bold;
  color: ${theme.palette.common.black};
`

styles.error = css`
  color: ${theme.palette.error.main};
  margin: 0px 30px 15px 30px;
`

styles.emailsContainer = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin: 18px 0px;
  color: ${theme.palette.common.black};
`

styles.descriptionContainer = css`
  width: 100%;
  height: 40px;
  margin: 14px 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.palette.common.black};
`

styles.label = css`
  width: 30%;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

styles.sliderContainer = css`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0px 16px;
  font-size: 14px;
`

styles.description = css`
  width: 15%;
  display: flex;
  align-items: center;
  font-size: 14px;
`

styles.selectForm = css`
  width: 100%;
  height: 40px;
  font-weight: 500;
  background: transparent;

  &:focus {
    outline: 2px solid #4a90e2; /* example color */
    outline-offset: 2px; /* optional: separates the outline from the element */
  }
`

styles.actionContainer = css`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 10px;
`

styles.actionButton = css`
  height: 24px;
  padding: 0px 20px;
  text-transform: none;
  font-size: 14px;
  background: ${theme.palette.common.blue};
  color: ${theme.palette.common.white};

  &:hover {
    background: ${theme.palette.common.blue2};
  }
`

styles.cancelButton = css`
  height: 24px;
  padding: 0px 20px;
  margin-right: 15px;
  text-transform: none;
  font-size: 14px;
  background: transparent;
  color: ${theme.palette.common.blue};
  box-shadow: none;

  &:hover {
    background: transparent;
  }
`

styles.loaderContainer = css`
  display: flex;
  color: white;
  margin-right: 10px;
`

styles.error = css`
  color: ${theme.palette.error.main};
  height: 20px;
  text-align: right;
  font-size: 14px;
`

styles.iconClose = css`
  position: absolute;
  display: flex;
  color: grey;
  top: 20px;
  right: 24px;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: 28px;
  cursor: pointer;
`

const CssSlider = withStyles({
  root: {
    color: '#8800FF',
    '& .MuiSlider-thumb.MuiSlider-active': {
      boxShadow: '0px 0px 0px 14px #8800ff29'
    },
    '& .MuiSlider-thumb.Mui-focusVisible': {
      boxShadow: '0px 0px 0px 0px transparent'
    },
    '& .MuiSlider-thumb:hover': {
      boxShadow: '0px 0px 0px 8px #8800ff29'
    }
  }
})(Slider)

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: theme.palette.common.black
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .Mui-disabled': {
      color: `${theme.palette.text.disabled} !important`
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '12px',
      color: theme.palette.common.black
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${theme.palette.text.disabled} !important`,
        borderRadius: '8px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 8px',
      textAlign: 'center',
      color: theme.palette.common.black
    }
  }
})(TextField)

const CssButton = withStyles({
  root: {
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)'
    }
  }
})(Button)

const inputText = {
  input: { color: theme.palette.common.black },
  label: { color: theme.palette.common.black },
  width: '100%',
  marginTop: 0,
  marginBottom: 0
}

function ManageOrganizationMinutesModal(props) {
  const [minutesById, setMinutesById] = React.useState({})
  const [minutesByIdWhileDragging, setMinutesByIdWhileDragging] = React.useState(0)
  const [isEnabled, setIsEnabled] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [showError, setShowError] = React.useState('')
  const [indexDragging, setIndexDragging] = React.useState(-1)

  React.useEffect(() => {
    const initialMinutes = {}
    props.organizations.forEach(v => {
      initialMinutes[v._id] = v.tracking_minutes || 0
    })
    setMinutesById(initialMinutes)
  }, [props.organizations])

  function onMinutesChange(event, value, organization) {
    const previousMinutes = minutesById[organization._id] || 0
    const difference = value - previousMinutes
    const availableMinutes = Math.max(0, minutesById[props.ownerOrganization._id] - difference)

    const newMinutesById = {
      ...minutesById,
      [props.ownerOrganization._id]: availableMinutes,
      [organization._id]: value
    }

    setMinutesById(newMinutesById)

    const minutesHaveChanged =
      props.organizations.length > 1 &&
      Object.keys(newMinutesById).some(
        v => newMinutesById[v] !== props.organizations.find(org => org._id === v).tracking_minutes
      )
    setShowError('')
    setIsEnabled(minutesHaveChanged)
  }

  function onSliderChangeStart(index) {
    // Save current state when starting to drag to not update other sliders while dragging.
    setIndexDragging(index)
    setMinutesByIdWhileDragging({ ...minutesById })
  }

  function onSliderChangeEnd() {
    setIndexDragging(-1)
  }

  async function onSubmit() {
    // On submit, show loader and disable submit to not perform many retries with the same key.
    setIsLoading(true)
    setIsEnabled(false)

    try {
      const payload = { minutesById }
      await OrganizationService.updateMinutes(props.ownerOrganization._id, payload)

      // Reload UI.
      await Actions.updateUser()
      onClose()
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
      if (message) {
        setShowError(message)
        if (!showError) {
          setTimeout(() => setShowError(''), 5000)
        }

        setIsLoading(false)
      } else {
        console.error(error)
        toast.error(window.I18n.t('groups.minutesErrorMessage'))
        onClose()
      }
    }
  }

  function onClose() {
    props.onApply()
  }

  return (
    <div css={styles.blockingOverlay}>
      <div css={styles.box}>
        <span css={styles.title}>{window.I18n.t('groups.manageGameCloudMinutes')}</span>
        {props.organizations.map((organization, i) => {
          const organizationName =
            i > 0 ? organization.name : window.I18n.t('groups.availableMinutes')
          const availableMinutes = minutesById[props.ownerOrganization._id] || 0
          const availableMinutesWhileDragging =
            minutesByIdWhileDragging[props.ownerOrganization._id] || 0
          const minutes = minutesById[organization._id] || 0
          const minutesWhileDragging = minutesByIdWhileDragging[organization._id] || 0
          const min = i > 0 ? 0 : -1
          const max =
            i > 0 && indexDragging > 0 && indexDragging !== i
              ? minutesWhileDragging + availableMinutesWhileDragging
              : minutes + (i > 0 ? availableMinutes : 0)

          let step
          if (max <= 50) {
            step = 1
          } else if (max <= 500) {
            step = 2
          } else if (max <= 5000) {
            step = 5
          } else {
            step = 10
          }

          return (
            <div css={styles.descriptionContainer}>
              <span css={styles.label}>{organizationName}</span>
              <span css={styles.sliderContainer}>
                <CssSlider
                  disabled={i === 0}
                  value={minutes}
                  min={min}
                  max={max}
                  step={step}
                  onChange={(event, value) => onMinutesChange(event, value, organization)}
                  onPointerDown={() => onSliderChangeStart(i)}
                  onPointerUp={onSliderChangeEnd}
                />
              </span>
              <span css={styles.description}>
                {/* Numbers only */}
                <CssTextField
                  disabled={i === 0}
                  variant="outlined"
                  placeholder={'0'}
                  value={minutes.toFixed(2)}
                  onChange={event => {
                    const value = Math.min(Math.max(0, parseFloat(event.target.value)), max)
                    if (!isNaN(value)) {
                      onMinutesChange(event, value, organization)
                    } else {
                      // Reset to 0 if not a number.
                      onMinutesChange(event, 0, organization)
                    }
                  }}
                  type="text"
                  name="minutes"
                  margin="normal"
                  css={inputText}
                />
              </span>
            </div>
          )
        })}

        <div css={styles.error}>{showError}</div>
        <div css={styles.actionContainer}>
          <CssButton
            color="primary"
            variant="contained"
            css={styles.cancelButton}
            disabled={isLoading}
            onClick={onClose}
          >
            {window.I18n.t('common.cancel')}
          </CssButton>
          <CssButton css={styles.actionButton} disabled={!isEnabled} onClick={onSubmit}>
            {isLoading && (
              <div css={styles.loaderContainer}>
                <CircularProgress size={16} thickness={4} color="inherit" />
              </div>
            )}
            {window.I18n.t('common.save')}
          </CssButton>
        </div>
        <span
          css={styles.iconClose}
          style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          onClick={onClose}
        >
          {' '}
          x{' '}
        </span>
      </div>
    </div>
  )
}

ManageOrganizationMinutesModal.defaultProps = {}

ManageOrganizationMinutesModal.propTypes = {
  user: PropTypes.object.isRequired,
  ownerOrganization: PropTypes.object.isRequired,
  organizations: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired
}

export default ManageOrganizationMinutesModal
